import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import VendorStoreService from "../../services/VendorStoreService";
import VendorService from "../../services/VendorServices";
import MasterLocationService from "../../services/MasterLocationService";
import Breadcrumb from "../../components/Breadcrumb";
import StorageService from "../../services/StorageService";
import MessageDialog from "../../components/MessageDialog";

export default function AddVendorStore() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const [editMode, setEditMode] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const country = watch("countryId");
  const state = watch("state");

  const showMessage = (text, type = "success") => {
    setMessage(text);
    setMsgType(type);
  };

  // Load Active Vendors for dropdown
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const companyId = StorageService.activeCompany()?.companyId;
        if (companyId) {
          const res = await VendorService.getByCompanyId(companyId);
          setVendors(res?.data || res || []);
        }
      } catch (err) {
        console.error("Error loading vendors:", err);
        setVendors([]);
      }
    };
    loadVendors();
  }, []);

  // Load countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await MasterLocationService.getAllCountries();
        setCountries(data || []);
      } catch (err) {
        console.error("Error fetching countries:", err);
        setCountries([]);
      }
    };
    fetchCountries();
  }, []);

  // Dynamic States fetch
  useEffect(() => {
    if (country) {
      const fetchStates = async () => {
        try {
          const data = await MasterLocationService.getStatesByCountry(country);
          setStates(data || []);
          if (!editMode) {
            setValue("state", "");
            setCities([]);
            setValue("city", "");
          }
        } catch (err) {
          console.error("Error fetching states:", err);
          setStates([]);
          setCities([]);
          setValue("state", "");
          setValue("city", "");
        }
      };
      fetchStates();
    } else {
      setStates([]);
      setCities([]);
      if (!editMode) {
        setValue("state", "");
        setValue("city", "");
      }
    }
  }, [country, setValue, editMode]);

  // Dynamic Cities fetch
  useEffect(() => {
    if (state) {
      const fetchCities = async () => {
        try {
          const data = await MasterLocationService.getCitiesByState(state);
          setCities(data || []);
          if (!editMode) setValue("city", "");
        } catch (err) {
          console.error("Error fetching cities:", err);
          setCities([]);
          if (!editMode) setValue("city", "");
        }
      };
      fetchCities();
    } else {
      setCities([]);
      if (!editMode) setValue("city", "");
    }
  }, [state, setValue, editMode]);

  // Edit Mode Load
  useEffect(() => {
    const loadStore = async () => {
      if (!id) return;
      setEditMode(true);
      try {
        setEditLoading(true);
        const res = await VendorStoreService.getById(id);
        const store = res?.data || res;

        if (store) {
          setValue("vendorId", String(store.vendorId));
          setValue("vendorStoreName", store.vendorStoreName || "");
          setValue("GSTN", store.GSTN || "");
          setValue("address", store.address || "");
          setValue("address2", store.address2 || "");
          setValue("landMark", store.landMark || "");
          setValue("zip", store.zip || "");
          setValue("status", store.status || "Active");

          // Countries
          const countryList = await MasterLocationService.getAllCountries();
          setCountries(countryList || []);
          setValue("countryId", String(store.countryId));

          // States
          const stateList = await MasterLocationService.getStatesByCountry(store.countryId);
          setStates(stateList || []);
          setValue("state", String(store.state));

          // Cities
          const cityList = await MasterLocationService.getCitiesByState(store.state);
          setCities(cityList || []);
          setValue("city", String(store.city));
        }
      } catch (error) {
        console.error("Modify Load Error:", error);
        showMessage("Error loading details.", "error");
      } finally {
        setEditLoading(false);
      }
    };
    loadStore();
  }, [id, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      let res;
      const payload = {
        ...data,
        vendorId: Number(data.vendorId),
        countryId: Number(data.countryId),
        state: Number(data.state),
        city: Number(data.city),
      };

      if (editMode) {
        payload.updatedBy = StorageService.loginData().employeeId;
        res = await VendorStoreService.update(id, payload);
      } else {
        payload.companyId = StorageService.activeCompany()?.companyId;
        payload.createdBy = StorageService.loginData().employeeId;
        payload.status = "Active";
        res = await VendorStoreService.create(payload);
      }

      if (res?.status || res?.id) {
        showMessage(
          editMode ? "Vendor Store updated successfully." : "Vendor Store added successfully.",
          "success"
        );
        setTimeout(() => {
          navigate("/cpnl/vendorstore/view");
        }, 1000);
      } else {
        showMessage(res?.message || "Failed to save Vendor Store.", "error");
      }
    } catch (error) {
      console.error("Error saving vendor store:", error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error saving vendor store";
      showMessage(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    reset();
    navigate("/cpnl/vendorstore/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Vendor Store" : "Add Vendor Store"}
        secondTitle="Masters"
      />
      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading vendor store data...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Row 1 */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vendor <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("vendorId", { required: "Vendor is required" })}
                  >
                    <option value="">Select Vendor</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.vendorName} ({v.vendorCode})
                      </option>
                    ))}
                  </select>
                  {errors.vendorId && (
                    <small className="text-danger">{errors.vendorId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Vendor Store Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Vendor Store Name"
                    {...register("vendorStoreName", {
                      required: "Vendor Store Name is required",
                      minLength: { value: 3, message: "At least 3 characters" },
                    })}
                  />
                  {errors.vendorStoreName && (
                    <small className="text-danger">{errors.vendorStoreName.message}</small>
                  )}
                </div>
              </div>

              {/* Row 2 */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    GSTN
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="GSTN"
                    {...register("GSTN", {
                      validate: (value) => {
                        if (!value) return true;
                        return (
                          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(value) ||
                          "GSTN must be 15 characters valid format (e.g. 22AAAAA1111A1Z1)"
                        );
                      }
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                  {errors.GSTN && <small className="text-danger">{errors.GSTN.message}</small>}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Landmark</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Landmark"
                    {...register("landMark")}
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Address"
                    {...register("address")}
                  />
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">Address 2</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Address 2"
                    {...register("address2")}
                  />
                </div>
              </div>

              {/* Row 4 */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Country <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("countryId", { required: "Country is required" })}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.countryName}
                      </option>
                    ))}
                  </select>
                  {errors.countryId && (
                    <small className="text-danger">{errors.countryId.message}</small>
                  )}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    State <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("state", { required: "State is required" })}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.stateName}
                      </option>
                    ))}
                  </select>
                  {errors.state && <small className="text-danger">{errors.state.message}</small>}
                </div>
              </div>

              {/* Row 5 */}
              <div className="row">
                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    District <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    {...register("city", { required: "District is required" })}
                  >
                    <option value="">Select District</option>
                    {cities.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.cityName}
                      </option>
                    ))}
                  </select>
                  {errors.city && <small className="text-danger">{errors.city.message}</small>}
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label">
                    Zip
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Zip"
                    {...register("zip")}
                  />
                </div>
              </div>

              {/* Row 6 (Only visible in edit mode) */}
              {editMode && (
                <div className="row">
                  <div className="mb-3 col-md-6">
                    <label className="form-label">Status</label>
                    <select className="form-select" {...register("status")}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              )}

              <button type="submit" className="btn btn-primary mt-3" disabled={loading}>
                <i className="ti ti-circle-check me-1"></i>
                {loading ? "Saving..." : editMode ? "Update" : "Save"}
              </button>

              <button
                type="button"
                className="btn btn-secondary mt-3 ms-2"
                onClick={handleCancel}
              >
                <i className="ti ti-cancel me-1"></i>
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
